/**
 * Pay Period Detail Page (Admin)
 *
 * Displays pay period details with:
 * - Period info and status
 * - Summary statistics (total pay, hours, crew count)
 * - Edit and close actions
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getPayPeriod, closePayPeriod, deletePayPeriod } from '../../../api/payPeriods';
import { getErrorMessage } from '../../../api/client';
import type { PayPeriod, PayPeriodSummary, PayPeriodStatus } from '../../../types/models';
import PayPeriodFormModal from './PayPeriodFormModal';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';
import { toDateInputValue, formatInTimeZone } from '../../../utils/dateFormatting';

export default function PayPeriodDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // State
  const [payPeriod, setPayPeriod] = useState<PayPeriod | null>(null);
  const [summary, setSummary] = useState<PayPeriodSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCloseDialog, setShowCloseDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Fetch pay period
  const fetchPayPeriod = useCallback(async () => {
    if (!id) return;

    setLoading(true);
    setError(null);

    try {
      const response = await getPayPeriod(id);
      setPayPeriod(response.payPeriod);
      setSummary(response.summary);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchPayPeriod();
  }, [fetchPayPeriod]);

  // Handle close period
  const handleClosePeriod = async () => {
    if (!id) return;

    setIsClosing(true);
    setActionError(null);

    try {
      await closePayPeriod(id);
      setShowCloseDialog(false);
      fetchPayPeriod();
    } catch (err) {
      setActionError(getErrorMessage(err));
    } finally {
      setIsClosing(false);
    }
  };

  // Handle delete period
  const handleDeletePeriod = async () => {
    if (!id) return;

    setIsDeleting(true);
    setActionError(null);

    try {
      await deletePayPeriod(id);
      navigate('/admin/pay-periods');
    } catch (err) {
      setActionError(getErrorMessage(err));
      setIsDeleting(false);
    }
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    return formatInTimeZone(dateString, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Format currency
  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(cents / 100);
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-rack border-r-transparent" />
        <p className="mt-2 text-gray-600">Loading pay period...</p>
      </div>
    );
  }

  if (error || !payPeriod) {
    return (
      <div className="p-8 text-center">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg inline-block">
          {error || 'Pay period not found'}
        </div>
        <div className="mt-4">
          <Link to="/admin/pay-periods" className="text-primary-600 hover:text-primary-700">
            Back to Pay Periods
          </Link>
        </div>
      </div>
    );
  }

  // The reports read these as the period's local calendar days; the raw
  // instants would be one day off at the end (local end of day is past UTC
  // midnight west of UTC).
  const reportRangeQuery = new URLSearchParams({
    startDate: toDateInputValue(payPeriod.startDate),
    endDate: toDateInputValue(payPeriod.endDate),
  }).toString();

  return (
    <div>
      {/* Breadcrumb */}
      <nav className="mb-4">
        <Link to="/admin/pay-periods" className="text-primary-600 hover:text-primary-700 text-sm">
          &larr; Back to Pay Periods
        </Link>
      </nav>

      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-6">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900">{payPeriod.name}</h1>
            <StatusBadge status={payPeriod.status} />
          </div>
          <p className="text-gray-600 mt-1">
            {formatDate(payPeriod.startDate)} - {formatDate(payPeriod.endDate)}
          </p>
        </div>
        {payPeriod.status === 'OPEN' && (
          <div className="flex gap-3 mt-4 sm:mt-0">
            <button
              onClick={() => setShowEditModal(true)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Edit
            </button>
            <button
              onClick={() => setShowCloseDialog(true)}
              className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Close Period
            </button>
          </div>
        )}
      </div>

      {/* Action error */}
      {actionError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {actionError}
        </div>
      )}

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <SummaryCard
            label="Total Pay"
            value={formatCurrency(summary.totalPay)}
            icon={
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
          />
          <SummaryCard
            label="Total Hours"
            value={summary.totalHours.toFixed(1)}
            icon={
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
          />
          <SummaryCard
            label="Assignments"
            value={summary.assignmentCount.toString()}
            icon={
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            }
          />
          <SummaryCard
            label="Crew Members"
            value={summary.crewCount.toString()}
            icon={
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            }
          />
          <SummaryCard
            label="Events"
            value={summary.eventCount.toString()}
            icon={
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            }
          />
        </div>
      )}

      {/* Actions */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Actions</h2>
        <div className="space-y-3">
          <Link
            to={`/admin/reports/payroll?${reportRangeQuery}`}
            className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <svg className="w-5 h-5 text-gray-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span className="text-gray-700">View Payroll Report</span>
          </Link>
          <Link
            to={`/admin/reports/timesheet-export?${reportRangeQuery}`}
            className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <svg className="w-5 h-5 text-gray-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            <span className="text-gray-700">Export Timesheet</span>
          </Link>
          {payPeriod.status === 'OPEN' && (
            <button
              onClick={() => setShowDeleteDialog(true)}
              className="flex items-center w-full p-3 border border-red-200 rounded-lg hover:bg-red-50 transition-colors text-left"
            >
              <svg className="w-5 h-5 text-red-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              <span className="text-red-700">Delete Pay Period</span>
            </button>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      <PayPeriodFormModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSuccess={() => {
          setShowEditModal(false);
          fetchPayPeriod();
        }}
        payPeriod={payPeriod}
      />

      {/* Close Dialog */}
      <ConfirmDialog
        isOpen={showCloseDialog}
        onClose={() => setShowCloseDialog(false)}
        onConfirm={handleClosePeriod}
        title="Close Pay Period"
        message="Are you sure you want to close this pay period? Once closed, the period cannot be modified."
        confirmLabel="Close Period"
        isLoading={isClosing}
      />

      {/* Delete Dialog */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDeletePeriod}
        title="Delete Pay Period"
        message="Are you sure you want to delete this pay period? This action cannot be undone."
        confirmLabel="Delete"
        variant="danger"
        isLoading={isDeleting}
      />
    </div>
  );
}

// =============================================================================
// Sub-components
// =============================================================================

interface StatusBadgeProps {
  status: PayPeriodStatus;
}

function StatusBadge({ status }: StatusBadgeProps) {
  const styles: Record<PayPeriodStatus, string> = {
    OPEN: 'bg-green-100 text-green-800',
    CLOSED: 'bg-gray-100 text-gray-800',
  };

  const labels: Record<PayPeriodStatus, string> = {
    OPEN: 'Open',
    CLOSED: 'Closed',
  };

  return (
    <span className={`px-2.5 py-0.5 text-xs font-medium ${styles[status]}`}>
      {labels[status]}
    </span>
  );
}

interface SummaryCardProps {
  label: string;
  value: string;
  icon: React.ReactNode;
}

function SummaryCard({ label, value, icon }: SummaryCardProps) {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      <div className="flex items-center gap-3">
        <div className="text-gray-400">{icon}</div>
        <div>
          <p className="text-sm text-gray-500">{label}</p>
          <p className="text-lg font-semibold text-gray-900">{value}</p>
        </div>
      </div>
    </div>
  );
}
